// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.
import {inject} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot} from '@angular/router';
import {AuthService} from '../../services/auth-service';
import {JwtHelperService} from '@auth0/angular-jwt';
import {environment} from '../../environments/environment';
import {Observable, of} from 'rxjs';
import {SupportInfo} from '../../types/contact';
import {MatDialog} from '@angular/material/dialog';
import {AlertDialogComponent} from '../dialogs/alert/alert.component';
import {EVENT_TYPES} from '../../types/events';
import { IDecodedAuthConsoleToken, IDecodedAuthToken } from '../../types/auth';
import { TranslateService } from '@ngx-translate/core';
import authHelper from '../auth/authHelper';
import {D3eService} from '../../services/d3e-service';
import {UserService} from '../../services/user-service';
import {EventsService} from '../../services/events-service';
import {AppUpdateService} from '../../services/app-update.service';
import {ConfigurationService} from '../../services/configuration-service';

export const roleGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> => {

  const auth = inject(AuthService);
  const router = inject(Router);
  const jwtHelper = inject(JwtHelperService);
  const d3eService = inject(D3eService);
  const userService = inject(UserService);
  const dialog = inject(MatDialog);
  const eventsService = inject(EventsService);
  const appUpdateService = inject(AppUpdateService);
  const translate = inject(TranslateService);
  const configuration = inject(ConfigurationService);
  const authService = inject(AuthService);

  const willActivate = (route: ActivatedRouteSnapshot, decodedToken: IDecodedAuthToken): boolean => {
    // this will be passed from the route config
    // on the data property
    // const expectedRoles: string[] = route.data.expectedRoles && route.data.expectedRoles.split(',') || [];
    // const token = localStorage.getItem('token');
    // decode the token to get its payload
    // const tokenPayload: any = decode(token);
    // if (!auth.isAuthenticated() || expectedRoles.includes(tokenPayload.role)) {

    const requiresLogin = route.data.requiresLogin || false;

    if (requiresLogin && !auth.isAuthenticated()) {
      auth.logout_with_route(route);
      router.navigate(['/login']);
      return false;
    }

    const decodedConsoleToken: IDecodedAuthConsoleToken = decodedToken && decodedToken.con || null;

    const defaultPage = decodedConsoleToken && decodedConsoleToken.def || 'profile';
    // Infinite loop detected in the app.component, which set loopDetected
    // in localStorage with the offending url path.
    const loopDetectedItem = localStorage.getItem('loopDetected');
    if (loopDetectedItem) {
      let loopDetected = {'url': '', 'count': 0};
      try {
        loopDetected = JSON.parse(loopDetectedItem);
      } catch (error) {
        // Not a valid JSON object (should never happen), then no-op just continue with default loopDetected object.
        console.error(error);
      }

      if (loopDetected.count > 10) {
        eventsService.warning({
          type: EVENT_TYPES.NAVIGATION,
          description: 'Navigation issue detected.',
          detail: `An infinite loop was detected when the user ${decodedToken.usr || '<No User>'} 
                        was trying to reach the '${loopDetected.url}' page.`,
        }).subscribe();
        // If there is no token in localStorage, but there is a loopDetected in localStorage
        // it causes an error where the login page does not load so we need to remove the
        // loopDetected from localStorage and the login page will be loaded again.
        if (!decodedToken) {
          localStorage.removeItem('loopDetected');
        } else {
          const vals = translate.instant(['NAVIGATION_ISSUE_TITLE', 'NAVIGATION_ISSUE_CONTENT',
            'TRY_AGAIN', 'APP_HELP'], {
            url: loopDetected.url
          });
          const dialogRef = dialog.open(AlertDialogComponent, {
            width: '450px',
            data: {
              title: vals['NAVIGATION_ISSUE_TITLE'],
              content: vals['NAVIGATION_ISSUE_CONTENT'],
              rejectBtnName: vals['TRY_AGAIN'],
              acceptBtnName: vals['APP_HELP'],
              showRejectBtn: true,
            },
          });
          dialogRef.afterClosed().subscribe(result => {
            localStorage.removeItem('loopDetected');
            if (result) {
              window.open(SupportInfo.url, '_blank', 'noopener noreferrer');
            }
            // Send the user to the one place that is valid for every user, their profile page.
            router.navigate(['profile']);
          });
        }
        // Stop the infinite loop
        return false;
      }
    }

    if (route.url.length == 1 && route.url[0].path == 'update-request') {
      return true;
    }

    // Do not allow Lenovo users to access the subscriptions/select URL.
    if (route.url.length === 2
      && route.url[0].path === 'subscriptions'
      && route.url[1].path === 'select'
      && configuration.redirectOnSelectBuyURL) {
      router.navigate(['login']);
    }

    if (!(route.url.length === 1 && route.url[0].path === 'login' && route.queryParamMap.has('device'))) {
      auth.showNav.next(true);
      auth.showToolbar.next(true);
      auth.showBanners.next(true);
    }

    if (route.url.length === 1 && route.url[0].path === 'maintenance') {
      authService.maintenanceMode$.next(true);
      return true;
    }

    if (!requiresLogin && auth.isAuthenticated()) {
      try {
        if (route.url.length === 1 && route.url[0].path === 'login' && route.queryParamMap.has('device')) {
          // For indirect login.  The login page will handle the device query parameter and redirect.
          return true;
        }

        if (route.url.length === 2 && route.url[0].path === 'login') {
          // This gets the activationToken in the url not the license 'token' in localStorage.
          const activationToken = route.params['token'];
          // Using to see if this is a valid jwt token at least, this will
          // cause an exception if it is not and we can just continue normally.
          const decodedActivationToken = jwtHelper.decodeToken(activationToken);

          if (decodedActivationToken != null) {
            d3eService.add_activation_token(activationToken).subscribe(
              resp => {
                router.navigate(['licenses', resp]);
              },
              error => {
                // noop: just continue
                // tslint:disable-next-line:no-unused-expression
                environment.__DEV__ && console.error(error);
              }
            );
            // return true;
          }
        } else if (route.url.length === 1 && (route.url[0].path === 'cigent-news' || route.url[0].path === 'd3e-news')) {
          return true;
        } else if (route.url.length === 2 && (route.url[0].path === 'd3e-download' || route.url[0].path === 'download' || route.url[0].path === 'agent-download')) {
          return true;
        }
      } catch (error) {
        // Just continue with normal flow.
        // tslint:disable-next-line:no-unused-expression
        environment.__DEV__ && console.error(error);
      }

      router.navigate([defaultPage]);
      return true;
    }

    if (route.url.length === 0) {
      if (decodedConsoleToken && decodedToken.src === 0 && decodedConsoleToken.fpw) {
        router.navigate(['profile', 'contact']);
        return true;
      }

      if (decodedConsoleToken && decodedToken.src === 0 && decodedConsoleToken.ftf) {
        router.navigate(['profile', '2fa']);
        return true;
      }

      router.navigate([defaultPage]);
      return true;
    }

    if ( route.url.length >= 1 ) {
      const path = route.url[0].path;
      if (decodedConsoleToken && decodedConsoleToken.fpw) {

        if (path === 'login' || (path === 'profile' && route.url[1].path === 'contact')) {
          return true;
        }

        router.navigate(['profile', 'contact']);
        return true;
      }

      if (decodedConsoleToken && decodedConsoleToken.ftf) {

        if (path === 'login' || (path === 'profile' && route.url[1].path === 'contact')) {
          return true;
        }

        router.navigate(['profile', 'contact']);
        return true;
      }

      if (path === 'download' || path === 'd3e-download' || path === 'agent-download' || path === 'redirector') {
        return true;
      }

      if ( path === 'endpoints' ) {
        if (decodedConsoleToken && decodedConsoleToken.epp ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      if ( path === 'hosts' ) {
        return true;
      }

      if ( path === 'secure-ssd' ) {
        if (decodedConsoleToken && decodedConsoleToken.ssp ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      if ( path === 'device' ) {
        if (decodedConsoleToken && decodedConsoleToken.epp) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      if ( path === 'admin' ) {

        if (decodedConsoleToken && decodedConsoleToken.adp ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      if ( path === 'history' ) {
        if (decodedConsoleToken && decodedConsoleToken.thp ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      // TODO: This should be removed only after the devices sets page is actually removed.
      if ( path === 'device-sets' ) {
        router.navigate([defaultPage]);
        return false;
      }

      if ( path === 'groups' ) {
        if (decodedConsoleToken && decodedConsoleToken.grp ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      if ( path.startsWith('group-details') ) {
        if (decodedConsoleToken && decodedConsoleToken.grp ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      if ( path === 'policy' ) {
        if (decodedConsoleToken && decodedConsoleToken.pop ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      if ( path === 'encryption' ) {
        if (decodedConsoleToken && decodedConsoleToken.enp ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      if ( path === 'integrations' ||
        path === 'cisco-amp' ||
        path === 'cisco-duo' ||
        path === 'sophos' ||
        path === 'cyberark-epm' ||
        path === 'carbon-black' ||
        path === 'third-party' ||
        path === 'active-directory' ||
        path === 'okta'
      ) {
        if (decodedConsoleToken && decodedConsoleToken.inp ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      if ( path === 'organizations' ) {
        if (decodedConsoleToken && decodedConsoleToken.orp ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      // if ( path === 'dashboard' ) {
      //   if (decodedConsoleToken && decodedConsoleToken.dap ) {
      //     return true;
      //   } else {
      //     router.navigate([defaultPage]);
      //     return false;
      //   }
      // }

      if ( path === 'dashboard' ) {
        if (decodedConsoleToken && decodedConsoleToken.ddp ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      if ( path === 'events' ) {
        if (decodedConsoleToken && decodedConsoleToken.evp ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      if ( route.url.length === 1 && (path === 'subscriptions' ||
                                      path === 'assign') ) {
        if (decodedConsoleToken && decodedConsoleToken.sup ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      if ( route.url.length === 1 && path === 'products' ) {
        if (decodedConsoleToken && decodedConsoleToken.sup ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      if ( path === 'admin-settings' ) {
        if (decodedConsoleToken && decodedConsoleToken.asp ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      if ( path === 'users' ) {
        if (decodedConsoleToken && decodedConsoleToken.usp ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

      if ( path === 'downloads' ) {
        if (decodedConsoleToken && decodedConsoleToken.sup ) {
          return true;
        } else {
          router.navigate([defaultPage]);
          return false;
        }
      }

    }

    return true;
  };

  appUpdateService.checkForUpdate();

  if (route.url.length === 2 && route.url[0].path === 'okta' && route.url[1].path === 'callback') {
    return of(true);
  }

  // The tokenGetter() function is defined in the config of the JwtModule setting in the app.module.ts file.
  // It returns localStorage.getItem('token');
  const token = jwtHelper.tokenGetter();
  let decoded: IDecodedAuthToken;
  try {
    decoded = jwtHelper.decodeToken<IDecodedAuthToken>(token as string);
  } catch (error) {
    decoded = null;
  }

  if (decoded && route.url.length === 2) {
    if (route.url[0].path === 'invitation' ||
      route.url[0].path === 'verify-email' ||
      route.url[0].path === 'd3e-invitation' ||
      route.url[0].path === 'cigent-invitation' ||
      route.url[0].path === 'verify-free') {
      auth.logoutWithInvitation(route);
    }
  }

  const utcnow = (new Date()).getTime() / 1000;
  const activate = willActivate(route, decoded);
  if (decoded && decoded.con && decoded.exp - utcnow > 1800) {
    return of(activate);
  }
  if (activate) {
    if ((route.url.length === 1 && route.url[0].path === 'login') ||
      (route.url.length === 1 && route.url[0].path === 'reset') ||
      (route.url.length === 2 && route.url[0].path === 'd3e-download') ||
      (route.url.length === 2 && route.url[0].path === 'agent-download') ||
      (route.url.length === 2 && route.url[0].path === 'download') ||
      (route.url.length === 2 && route.url[0].path === 'subscriptions' && route.url[1].path === 'plustrial') ||
      (route.url.length === 1 && route.url[0].path === 'cigent-news') ||
      (route.url.length === 1 && route.url[0].path === 'd3e-news') ||
      (route.url.length === 2 && route.url[0].path === 'invitation') ||
      (route.url.length === 2 && route.url[0].path === 'd3e-invitation') ||
      (route.url.length === 2 && route.url[0].path === 'cigent-invitation') ||
      (route.url.length === 2 && route.url[0].path === 'verify-free') ||
      (route.url.length === 2 && route.url[0].path === 'verify-email') ||
      (route.url.length === 2 && route.url[0].path === 'downloaded-free') ||
      (route.url.length === 2 && route.url[0].path === 'maintenance') ||
      (route.url.length === 1 && route.url[0].path === 'redirector')) {
      return of(true);
    }

    if (auth.isAuthenticated() && !authHelper.isPasswordExpired()) {
      userService.renewToken().subscribe(tokens => {
        localStorage.setItem('token', tokens.token);
        localStorage.setItem('mapToken', tokens.mapToken);
        auth.token$.next(tokens.token);
        auth.mapToken$.next(tokens.mapToken);
      });
    }

    return of(true);
  } else {
    return of(false);
  }

};
